import { selectedSpaceAtom } from "@/atoms/spaces"
import { meAtom } from "@/atoms/users"
import AccessDenied from "@/core/components/AccessDenied"
import { SupplierApps } from "@/features/apps/SupplierApps"
import { CustomerType } from "@/modules/space/constants/customerType"
import { Space } from "@/modules/spaces/models/space"
import { customerTypeNavVisibility } from "../utils/customerTypeNav"
import { IAuthUser } from "@/types/auth"
import { requireAuth } from "@/utils/browserStorage"
import { NextRouter } from "next/router"
import { useEffect, useMemo, useState } from "react"
import { useRecoilValue } from "recoil"

const routePermissions = {
  "/home": (space: Space) => space.settings.enable_approval_dashboard,
  "/leads": (space: Space) => !space.settings.disabled_leads,
  "/inbox": (space: Space) => !space.settings.disabled_messages,
  "/shared": (space: Space) => !space.settings.disabled_internal_proposals,
  "/supplier/opportunities": (space: Space) =>
    space.isOrganization ? !space.settings.disabled_external_proposals : true,
  "/resources": (space: Space) =>
    customerTypeNavVisibility(space.customerType).resources &&
    !space.settings.disabled_resources,
  "/consultants": (space: Space) =>
    customerTypeNavVisibility(space.customerType).consultants &&
    !space.settings.disabled_consultants,
  "/assignments": (space: Space) =>
    customerTypeNavVisibility(space.customerType).assignments,
  "/companies": (space: Space) =>
    space.isOrganization ? !space.settings.disabled_companies : true,
  "/agreements": (space: Space) => !space.settings.disabled_agreements,
  "/contracts": (space: Space) =>
    space.isOrganization ? !space.settings.disabled_contracts : true,
  "/analytics": (space: Space) => !space.settings.disabled_analytics,
  "/b2e-analytics": (space: Space) => !space.settings.disabled_b2b_analytics,
  "/timeline": (space: Space) => !space.settings.disabled_timeline,
  "/time_registration": (space: Space) => !space.settings.disabled_time_sheet,
  "/admin/dashboard": (space: Space, user: IAuthUser) =>
    space.uid === "a418e91f-452a-4a0a-b905-447e2187758f" &&
    user.email.endsWith("@flextribe.io"),
}

function RouteGuard({
  children,
  router,
}: {
  children: React.ReactNode
  router: NextRouter
}) {
  const selectedSpace = useRecoilValue(selectedSpaceAtom)
  const user = useRecoilValue(meAtom)

  const isPublicRoute = useMemo(
    () => router.pathname === "/login",
    [router.pathname],
  )
  const [isAllowed, setIsAllowed] = useState<boolean>(isPublicRoute)

  useEffect(() => {
    if (isPublicRoute) {
      setIsAllowed(true)
      return
    }

    const ok = requireAuth()
    setIsAllowed(ok)
  }, [isPublicRoute, router.asPath])

  if (!isAllowed) return null

  // block if space is organization and customer type is not B2B and user is not employee, kam or contact
  if (
    selectedSpace.isOrganization &&
    selectedSpace.customerType !== CustomerType.B2B &&
    !user?.is_employee &&
    !user?.has_kam_access &&
    !user?.is_contact
  ) {
    return <AccessDenied />
  }

  const isBlocked =
    routePermissions[router.pathname] &&
    !routePermissions[router.pathname](selectedSpace, user)

  if (isBlocked) return <SupplierApps />
  return children
}

export default RouteGuard
